import { useEffect, useRef, useMemo } from "react";
import { Loader2, Plus, Building2 } from "lucide-react";
import SpyAdCard from "./SpyAdCard";
import { useAdsSpyStore } from "../store/adsSpyStore";
import type { SpyAd } from "../types/adsSpy.types";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

interface AdsSpyGridProps {
  ads: SpyAd[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onClickDetail: (id: number) => void;
  onToggleBookmark: (id: number) => void;
  onCreateSimilar?: (ad: SpyAd) => void;
  hasBrands?: boolean;
  onAddBrand?: () => void;
}

export default function AdsSpyGrid({
  ads,
  loading,
  hasMore,
  onLoadMore,
  onClickDetail,
  onToggleBookmark,
  onCreateSimilar,
  hasBrands = true,
  onAddBrand,
}: AdsSpyGridProps) {
  const { language } = useAuth();
  const lang = language as Language;
  const t = (key: string, fallback: string) => dictionary[key]?.[lang] ?? fallback;
  const { filters, expiredAdIds, markAdExpired } = useAdsSpyStore();

  const filteredAds = useMemo(() => {
    if (!filters.status) return ads;
    if (filters.status === "expired") return ads.filter((ad) => expiredAdIds.has(ad.id));
    if (filters.status === "active") return ads.filter((ad) => !expiredAdIds.has(ad.id));
    return ads;
  }, [ads, filters.status, expiredAdIds]);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore]);

  if (loading && filteredAds.length === 0) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-fluxi" />
      </div>
    );
  }

  if (!loading && filteredAds.length === 0) {
    if (!hasBrands) {
      return (
        <div className="flex flex-col items-center justify-center py-28">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 bg-[#EEF0FF]">
            <Building2 className="w-7 h-7 text-fluxi" />
          </div>
          <p className="text-sm font-semibold text-gray-700">
            {t("add-a-brand-to-start", "Add a brand to get started")}
          </p>
          <p className="text-xs mt-1.5 text-gray-400 text-center max-w-xs">
            {t("to-spy-ads-add-brand", "To spy on ads, you first need to add at least one competitor brand.")}
          </p>
          {onAddBrand && (
            <button
              onClick={onAddBrand}
              className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-all duration-200 hover:shadow-lg hover:shadow-primary/20"
              style={{ background: "linear-gradient(to right, #4368E5, #4368E5)" }}
            >
              <Plus className="h-4 w-4" />
              {t("add-brand-lowercase", "Add brand")}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center py-28">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 bg-gray-100">
          <svg
            className="w-7 h-7 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-500">
          {t("no-ads-yet", "No ads yet")}
        </p>
        <p className="text-xs mt-1.5 text-gray-400">
          {t("scrape-brands-to-see-ads", "Scrape your brands to see their ads")}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
        {filteredAds.map((ad) => (
          <SpyAdCard
            key={ad.id}
            ad={ad}
            onClickDetail={onClickDetail}
            onToggleBookmark={onToggleBookmark}
            onCreateSimilar={onCreateSimilar}
            onMediaError={markAdExpired}
          />
        ))}
      </div>

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="flex justify-center py-10">
        {loading && filteredAds.length > 0 && (
          <Loader2 className="w-5 h-5 animate-spin text-fluxi" />
        )}
        {!hasMore && filteredAds.length > 0 && (
          <p className="text-xs text-gray-400">{t("no-more-ads", "No more ads")}</p>
        )}
      </div>
    </>
  );
}
